import { type FormEvent, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Key as KeyIcon,
  Lock as LockIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  AlertCircle as AlertCircleIcon,
  CheckCircle as CheckCircleIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { authService } from "@/services/auth";
import {
  useSiteConfigStore,
  selectProductName,
  selectLogoUrl,
} from "@/stores/siteConfigStore";
import { usePageTitle } from "@/hooks/usePageTitle";
import LanguageSelector from "@/components/LanguageSelector";
import styles from "./UpdatePassword.module.css";

export default function UpdatePassword() {
  const { t } = useTranslation();
  usePageTitle(t("pageTitle.updatePassword"));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const key = searchParams.get("key") || "";

  const productName = useSiteConfigStore(selectProductName);
  const logoUrl = useSiteConfigStore(selectLogoUrl);
  const hasLogo = !!logoUrl;

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const passwordMismatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password !== confirmPassword;

  const isFormValid =
    password.trim().length >= 6 && confirmPassword.trim().length >= 6;

  useEffect(() => {
    if (!key) {
      setError(
        t(
          "auth.update.invalidLink"
        ),
      );
    }
  }, [key, t]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (passwordMismatch || !isFormValid || !key) return;

    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const res = await authService.updatePassword(key, password);
      if (res.success) {
        setSuccess(res.message);
        setTimeout(() => navigate("/auth/login", { replace: true }), 2500);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("auth.update.error"),
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={styles.updatePasswordPage}>
      <div className={styles.languageSelectorContainer}>
        <LanguageSelector />
      </div>

      <div className={styles.updatePasswordForm}>
        <div className={styles.formHeader}>
          <div className={styles.logo}>
            {hasLogo ? (
              <img
                src={logoUrl!}
                alt={productName}
                className={styles.logoImage}
              />
            ) : (
              <div className={styles.logoIcon}>
                <KeyIcon size={32} />
              </div>
            )}
            <h1>{t("auth.update.title")}</h1>
          </div>
          <p className={styles.formSubtitle}>
            {t(
              "auth.update.subtitle"
            )}
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.formLabel}>
              {t("auth.update.passwordLabel")}
            </label>
            <div className={styles.inputContainer}>
              <LockIcon size={20} className={styles.inputIcon} />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={styles.input}
                placeholder={t(
                  "auth.update.passwordPlaceholder"
                )}
                required
                minLength={6}
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className={styles.passwordToggle}
                disabled={isLoading}
                aria-label={showPassword ? t("common.hide") : t("common.show")}
              >
                {showPassword ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
              </button>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirm-password" className={styles.formLabel}>
              {t("auth.update.confirmPasswordLabel")}
            </label>
            <div className={styles.inputContainer}>
              <LockIcon size={20} className={styles.inputIcon} />
              <input
                id="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={styles.input}
                placeholder={t(
                  "auth.update.confirmPasswordPlaceholder"
                )}
                required
                minLength={6}
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                className={styles.passwordToggle}
                disabled={isLoading}
                aria-label={showConfirmPassword ? t("common.hide") : t("common.show")}
              >
                {showConfirmPassword ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
              </button>
            </div>
            {passwordMismatch ? (
              <span className={styles.errorText}>
                {t("auth.update.passwordsMismatch")}
              </span>
            ) : null}
          </div>

          <button
            type="submit"
            className={styles.submitBtn}
            disabled={isLoading || !isFormValid || passwordMismatch || !key}
          >
            {isLoading ? <div className={styles.spinner} /> : <KeyIcon size={20} />}
            {isLoading
              ? t("auth.update.updating")
              : t("auth.update.submitButton")}
          </button>

          {error ? (
            <div className={styles.errorMessage} role="alert">
              <AlertCircleIcon size={16} />
              {error}
            </div>
          ) : null}

          {success ? (
            <div className={styles.successMessage} role="status">
              <CheckCircleIcon size={16} />
              {success}
            </div>
          ) : null}
        </form>
      </div>
    </div>
  );
}
